import axios from "axios";

// Relative baseURL keeps this same-origin regardless of what
// REACT_APP_BACKEND_URL resolves to — see src/index.js for why.
const client = axios.create({ baseURL: "/api", withCredentials: true });

export const listWellsides = () => client.get("/admin/wellsides").then((r) => r.data);
export const getWellside = (id) => client.get(`/admin/wellsides/${id}`).then((r) => r.data);
export const getWellsideBySlug = (slug) => client.get(`/admin/wellsides/by-slug/${slug}`).then((r) => r.data);
export const createWellside = (payload) => client.post("/admin/wellsides", payload).then((r) => r.data);
export const updateWellside = (id, payload) => client.put(`/admin/wellsides/${id}`, payload).then((r) => r.data);
export const deleteWellside = (id) => client.delete(`/admin/wellsides/${id}`).then((r) => r.data);
export const reorderWellsides = (orderedIds) => client.put("/admin/wellsides/reorder", { ordered_ids: orderedIds }).then((r) => r.data);
export const updateGallery = (id, gallery) => client.put(`/admin/wellsides/${id}/gallery`, { gallery }).then((r) => r.data);
export const uploadGalleryImage = (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return client.post(`/admin/wellsides/${id}/gallery/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return client.post("/admin/uploads", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---------------------------------------------------------------------------
// Generic product endpoints — used by every category migrated beyond the
// Wellside pilot (Roof Tents, Awnings, ...). See categoryConfig.js.
// ---------------------------------------------------------------------------
export const listProducts = (category) => client.get(`/admin/products/${category}`).then((r) => r.data);
export const getProductBySlug = (category, slug) => client.get(`/admin/products/${category}/by-slug/${slug}`).then((r) => r.data);
export const createProduct = (category, payload) => client.post(`/admin/products/${category}`, payload).then((r) => r.data);
export const updateProduct = (category, id, payload) => client.put(`/admin/products/${category}/${id}`, payload).then((r) => r.data);
export const deleteProduct = (category, id) => client.delete(`/admin/products/${category}/${id}`).then((r) => r.data);
export const reorderProducts = (category, orderedIds) => client.put(`/admin/products/${category}/reorder`, { ordered_ids: orderedIds }).then((r) => r.data);
export const importProducts = (category, products) => client.post(`/admin/products/${category}/import`, { products }).then((r) => r.data);
export const updateProductGallery = (category, id, gallery) => client.put(`/admin/products/${category}/${id}/gallery`, { gallery }).then((r) => r.data);
export const uploadProductGalleryImage = (category, id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return client.post(`/admin/products/${category}/${id}/gallery/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};


// ---------------------------------------------------------------------------
// Shipping Rates — flat NZ-domestic rates assignable to any product across
// both the generic `products` collection and the standalone Wellside Canopies
// collection.
// ---------------------------------------------------------------------------
export const listShippingRates = () => client.get("/admin/shipping-rates").then((r) => r.data);
export const createShippingRate = (payload) => client.post("/admin/shipping-rates", payload).then((r) => r.data);
export const updateShippingRate = (id, payload) => client.put(`/admin/shipping-rates/${id}`, payload).then((r) => r.data);
export const deleteShippingRate = (id) => client.delete(`/admin/shipping-rates/${id}`).then((r) => r.data);
export const getShippingRateAssignments = (id) => client.get(`/admin/shipping-rates/${id}/assignments`).then((r) => r.data);
export const assignShippingRate = (items, shippingRateId) => client.put("/admin/shipping-rates/assign", { items, shippingRateId }).then((r) => r.data);


// ---------------------------------------------------------------------------
// Site Settings — storefront category display order (Home page tiles + Shop/
// Bushtech nav dropdowns), editable from the Admin Panel Category Order page.
// ---------------------------------------------------------------------------
export const getSiteSettings = () => client.get("/site-settings").then((r) => r.data);
export const updateSiteSettings = (categoryOrder) => client.put("/admin/site-settings", { categoryOrder }).then((r) => r.data);


// ---------------------------------------------------------------------------
// Legal / Policy Pages — Privacy, Shipping & Returns copy, editable from the
// Admin Panel instead of being hardcoded into the page components.
// ---------------------------------------------------------------------------
export const listLegalPages = () => client.get("/admin/legal-pages").then((r) => r.data);
export const getLegalPage = (slug) => client.get(`/admin/legal-pages/${slug}`).then((r) => r.data);
export const updateLegalPage = (slug, title, sections) => client.put(`/admin/legal-pages/${slug}`, { title, sections }).then((r) => r.data);


// ---------------------------------------------------------------------------
// Analytics — self-hosted Sales / Traffic / Behaviour / Abandoned Carts
// dashboard, no external service required.
// ---------------------------------------------------------------------------
export const getAnalyticsSales = (range) => client.get("/admin/analytics/sales", { params: { range } }).then((r) => r.data);
export const getAnalyticsTraffic = (range) => client.get("/admin/analytics/traffic", { params: { range } }).then((r) => r.data);
export const getAnalyticsReturningVisitors = (range) => client.get("/admin/analytics/returning-visitors", { params: { range } }).then((r) => r.data);
export const getAnalyticsReturningVisitorsByProduct = (range) => client.get("/admin/analytics/returning-visitors-by-product", { params: { range } }).then((r) => r.data);
export const getAnalyticsReturningVisitorsByProductDetail = (range, { categorySlug, slug, path } = {}) =>
  client.get("/admin/analytics/returning-visitors-by-product/detail", { params: { range, category_slug: categorySlug, slug, path } }).then((r) => r.data);
export const getAnalyticsBehaviour = (range) => client.get("/admin/analytics/behaviour", { params: { range } }).then((r) => r.data);
export const getAnalyticsProductReturningVisitors = (categorySlug, slug, range) => client.get("/admin/analytics/product-returning-visitors", { params: { category_slug: categorySlug, slug, range } }).then((r) => r.data);
export const getAnalyticsPageReturningVisitors = (path, range) => client.get("/admin/analytics/page-returning-visitors", { params: { path, range } }).then((r) => r.data);
export const getAnalyticsPageTrend = (path, range) => client.get("/admin/analytics/page-trend", { params: { path, range } }).then((r) => r.data);
export const getAnalyticsWeeklyDigestPreview = () => client.get("/admin/analytics/weekly-digest-preview").then((r) => r.data);
export const getAnalyticsAbandonedCarts = () => client.get("/admin/analytics/abandoned-carts").then((r) => r.data);
export const getBushCabinBuilds = () => client.get("/admin/bushcabin-builds").then((r) => r.data);
export const getBushCabinBuildsSummary = (range) => client.get("/admin/bushcabin-builds/summary", { params: { range } }).then((r) => r.data);
export const getAbandonedCartDetail = (sessionId) => client.get(`/admin/analytics/abandoned-carts/${sessionId}`).then((r) => r.data);
export const getAnalyticsLive = () => client.get("/admin/analytics/live").then((r) => r.data);
export const getAnalyticsLiveHistory = (sessionId) => client.get(`/admin/analytics/live/${sessionId}/history`).then((r) => r.data);
export const resetAnalytics = () => client.post("/admin/analytics/reset").then((r) => r.data);

// Real GA4 Measurement ID — saved via Admin → Settings, dormant until the
// admin flips `gaEnabled` on (see AdminSettingsPage.jsx). Separate from the
// native analytics dashboard above.
export const updateGASettings = (gaMeasurementId, gaEnabled) => client.put("/admin/site-settings/google-analytics", { gaMeasurementId, gaEnabled }).then((r) => r.data);
export const updateMetaPixelSettings = (metaPixelId, metaPixelEnabled) => client.put("/admin/site-settings/meta-pixel", { metaPixelId, metaPixelEnabled }).then((r) => r.data);
